import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ContactFAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqCategories = [
    {
      id: 'engagement',
      title: 'Engagement Models',
      icon: 'Handshake',
      faqs: [
        {
          question: 'What engagement models do you offer?',
          answer: `We offer flexible engagement models to suit different project needs:\n\n• **Fixed-Price Projects**: Defined scope with predictable costs\n• **Time & Materials**: Flexible approach for evolving requirements\n• **Retainer Agreements**: Ongoing support and advisory services\n• **Hybrid Models**: Combination approach for complex initiatives\n\nOur team will recommend the best model based on your project scope, timeline, and budget requirements.`
        },
        {
          question: 'How do you ensure project success?',
          answer: `Our success methodology includes:\n\n• **Comprehensive Discovery**: Deep understanding of your challenges\n• **Stakeholder Alignment**: Clear communication and expectations\n• **Agile Delivery**: Iterative approach with regular checkpoints\n• **Risk Management**: Proactive identification and mitigation\n• **Change Management**: Ensuring adoption and sustainability\n\nWe maintain a 95% client satisfaction rate through this proven approach.`
        },
        {
          question: 'What is your typical project timeline?',
          answer: `Project timelines vary based on scope and complexity:\n\n• **Assessment Projects**: 2-4 weeks\n• **Process Optimization**: 3-6 months\n• **Digital Transformation**: 6-18 months\n• **Strategic Consulting**: 3-12 months\n\nWe provide detailed timeline estimates during our initial consultation, including key milestones and dependencies.`
        }
      ]
    },
    {
      id: 'pricing',
      title: 'Pricing & Investment',
      icon: 'DollarSign',
      faqs: [
        {
          question: 'How do you structure your pricing?',
          answer: `Our pricing is transparent and value-based:\n\n• **Project-Based**: Fixed price for defined deliverables\n• **Hourly Rates**: $150-$400 per hour based on expertise level\n• **Monthly Retainers**: $10K-$50K for ongoing support\n• **Value-Based**: Pricing tied to measurable outcomes\n\nWe provide detailed proposals with clear cost breakdowns and payment terms.`
        },
        {
          question: 'What factors influence project cost?',
          answer: `Several factors determine project investment:\n\n• **Project Scope**: Complexity and breadth of requirements\n• **Timeline**: Urgency and resource allocation needs\n• **Team Composition**: Mix of senior and junior resources\n• **Technology Requirements**: Tools and platform needs\n• **Change Management**: Level of organizational support needed\n\nWe optimize costs while ensuring quality delivery and sustainable outcomes.`
        },
        {
          question: 'Do you offer payment flexibility?',
          answer: `Yes, we offer various payment options:\n\n• **Milestone-Based**: Payments tied to deliverable completion\n• **Monthly Installments**: Spread costs over project duration\n• **Upfront Discounts**: Reduced rates for full payment\n• **Performance-Based**: Payments linked to achieved outcomes\n\nWe work with your finance team to structure payments that align with your cash flow needs.`
        }
      ]
    },
    {
      id: 'process',
      title: 'Process & Methodology',
      icon: 'Settings',
      faqs: [
        {
          question: 'What is your project methodology?',
          answer: `We follow a proven 5-phase methodology:\n\n• **Discovery**: Understanding current state and requirements\n• **Strategy**: Developing roadmap and solution architecture\n• **Design**: Creating detailed plans and specifications\n• **Implementation**: Executing with agile delivery practices\n• **Optimization**: Continuous improvement and support\n\nEach phase includes defined deliverables, checkpoints, and stakeholder reviews.`
        },
        {
          question: 'How do you handle project communication?',
          answer: `Communication is central to our approach:\n\n• **Weekly Status Reports**: Progress updates and metrics\n• **Bi-weekly Stakeholder Meetings**: Alignment and decision-making\n• **Monthly Executive Briefings**: High-level progress and outcomes\n• **Real-time Collaboration**: Slack, Teams, or preferred platforms\n• **Client Dashboard**: 24/7 access to project status and resources\n\nWe adapt our communication style to your organizational preferences.`
        },
        {
          question: 'What happens if project scope changes?',
          answer: `We handle scope changes through structured processes:\n\n• **Change Request Process**: Formal evaluation and approval\n• **Impact Assessment**: Timeline and cost implications\n• **Stakeholder Approval**: Clear authorization before proceeding\n• **Documentation Updates**: Revised project plans and contracts\n• **Communication**: All parties informed of changes\n\nOur agile approach accommodates reasonable scope evolution while maintaining project integrity.`
        }
      ]
    },
    {
      id: 'support',
      title: 'Support & Maintenance',
      icon: 'LifeBuoy',
      faqs: [
        {
          question: 'What post-project support do you provide?',
          answer: `Comprehensive post-project support includes:\n\n• **Warranty Period**: 90-day bug fixes and adjustments\n• **Knowledge Transfer**: Training and documentation\n• **Ongoing Support**: Maintenance and enhancement services\n• **Performance Monitoring**: Tracking outcomes and metrics\n• **Optimization Services**: Continuous improvement opportunities\n\nWe ensure sustainable success beyond project completion.`
        },
        {
          question: 'How quickly do you respond to support requests?',
          answer: `Our support response times are:\n\n• **Critical Issues**: 2-4 hours\n• **High Priority**: 4-8 hours\n• **Medium Priority**: 24 hours\n• **Low Priority**: 48-72 hours\n\nWe provide 24/7 support for critical systems and offer escalation procedures for urgent matters.`
        },
        {
          question: 'Can you provide training for our team?',
          answer: `Yes, we offer comprehensive training programs:\n\n• **End-User Training**: Hands-on system and process training\n• **Administrator Training**: Technical management and configuration\n• **Train-the-Trainer**: Building internal training capabilities\n• **Custom Workshops**: Tailored to specific needs and roles\n• **Online Resources**: Documentation, videos, and self-service materials\n\nTraining is included in most projects with additional options available.`
        }
      ]
    }
  ];

  const toggleFAQ = (categoryId, faqIndex) => {
    const faqId = `${categoryId}-${faqIndex}`;
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  return (
    // Changed section background to a light gray
    <section className="py-16 lg:py-24 bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          {/* Changed text to black */}
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 font-heading">
            Frequently Asked Questions
          </h2>
          {/* Changed text to a dark gray */}
          <p className="text-lg text-gray-800 max-w-2xl mx-auto">
            Get answers to common questions about our engagement models, pricing, processes, and support services.
          </p>
        </div>

        <div className="space-y-8">
          {faqCategories.map((category) => (
            // Changed glass-morph background to white and border to light gray
            <div key={category.id} className="glass-morph bg-white backdrop-blur-md border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-brand-cyan rounded-lg flex items-center justify-center mr-4">
                  {/* Icon color might need to be adjusted depending on its default */}
                  <Icon name={category.icon} size={24} className="text-white" />
                </div>
                {/* Changed text to black */}
                <h3 className="text-xl font-semibold text-black">{category.title}</h3>
              </div>

              <div className="space-y-4">
                {category.faqs.map((faq, index) => {
                  const faqId = `${category.id}-${index}`;
                  const isOpen = openFAQ === faqId;

                  return (
                    // Changed border to light gray
                    <div key={index} className="border border-gray-200 rounded-lg">
                      <button
                        // Adjusted hover background for light theme
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => toggleFAQ(category.id, index)}
                      >
                        {/* Changed text to black */}
                        <span className="font-medium text-black pr-4">{faq.question}</span>
                        {/* Changed icon color to a dark gray */}
                        <Icon
                          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          className="text-gray-600 flex-shrink-0"
                        />
                      </button>

                      {isOpen && (
                        <div className="px-6 pb-4">
                          {/* Changed border to light gray */}
                          <div className="pt-4 border-t border-gray-200">
                            {/* Changed text to dark gray */}
                            <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                              {faq.answer}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          {/* Changed glass-morph background to light-blue tint and border to a darker light-blue */}
          <div className="glass-morph bg-blue-50 backdrop-blur-sm border border-blue-200 rounded-xl p-8">
            {/* Changed text to black */}
            <h3 className="text-2xl font-bold text-black mb-4">
              Still Have Questions?
            </h3>
            {/* Changed text to dark gray */}
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Our team is ready to provide personalized answers to your specific questions and help you understand how we can support your transformation journey.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* Button colors remain as they use brand colors for text or backgrounds */}
              <button className="inline-flex items-center px-6 py-3 bg-brand-cyan text-white font-semibold rounded-lg hover:bg-brand-cyan/90 transition-colors duration-200">
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Start Live Chat
              </button>
              <button className="inline-flex items-center px-6 py-3 border border-brand-green text-brand-green font-semibold rounded-lg hover:bg-brand-green/10 transition-colors duration-200">
                <Icon name="Phone" size={20} className="mr-2" />
                Schedule Call
              </button>
              {/* Changed text to black and border to dark gray */}
              <button className="inline-flex items-center px-6 py-3 border border-gray-400 text-black font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200">
                <Icon name="Mail" size={20} className="mr-2" />
                Send Email
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFAQ;